import { useState, useEffect } from 'react'
import { adminApi } from '../lib/api'
import {
  Database,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  ExternalLink,
  RefreshCw,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'

type MigrationStatus = {
  summary: {
    total_tenants: number
    current: number
    behind: number
    unknown: number
    latest_version: string | null
  }
  tenants: Array<{
    tenant_id: string
    slug: string
    company_name: string
    tenant_status: string
    db_name: string
    current_version: string
    migration_status: string
    is_current: boolean
    last_migration_at: number | null
  }>
  available_migrations: Array<{
    version: string
    name: string
  }>
}

type MigrationRun = {
  id: string
  migration_version: string
  migration_name: string
  triggered_by: string
  trigger_type: string
  started_at: number
  completed_at: number
  status: string
  tenants_total: number
  tenants_success: number
  tenants_failed: number
  tenants_skipped: number
  dry_run: boolean
  github_run_url: string | null
}

export default function Migrations() {
  const [status, setStatus] = useState<MigrationStatus | null>(null)
  const [runs, setRuns] = useState<MigrationRun[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedRun, setExpandedRun] = useState<string | null>(null)
  const [runDetails, setRunDetails] = useState<Record<string, any>>({})

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    setLoading(true)
    try {
      const [statusRes, runsRes] = await Promise.all([
        adminApi.getMigrationStatus(),
        adminApi.getMigrationRuns(),
      ])

      if (statusRes.success) {
        setStatus({
          summary: statusRes.summary,
          tenants: statusRes.tenants,
          available_migrations: statusRes.available_migrations,
        })
      }

      if (runsRes.success) {
        setRuns(runsRes.runs)
      }
    } catch (err) {
      console.error('Failed to load migration data:', err)
    } finally {
      setLoading(false)
    }
  }

  async function loadRunDetails(runId: string) {
    if (runDetails[runId]) {
      setExpandedRun(expandedRun === runId ? null : runId)
      return
    }

    try {
      const res = await adminApi.getMigrationRun(runId)
      if (res.success) {
        setRunDetails((prev) => ({ ...prev, [runId]: res.tenant_details }))
        setExpandedRun(runId)
      }
    } catch (err) {
      console.error('Failed to load run details:', err)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-400" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-400" />
      case 'partial':
        return <AlertTriangle className="h-4 w-4 text-yellow-400" />
      case 'running':
        return <RefreshCw className="h-4 w-4 text-blue-400 animate-spin" />
      default:
        return <Clock className="h-4 w-4 text-gray-400" />
    }
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      completed: 'bg-green-500/20 text-green-400',
      success: 'bg-green-500/20 text-green-400',
      failed: 'bg-red-500/20 text-red-400',
      partial: 'bg-yellow-500/20 text-yellow-400',
      running: 'bg-blue-500/20 text-blue-400',
      skipped: 'bg-gray-500/20 text-gray-400',
      unknown: 'bg-gray-500/20 text-gray-400',
    }
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.unknown}`}>
        {status}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Database Migrations</h1>
          <p className="text-gray-400">Track schema versions across all tenant databases</p>
        </div>
        <button onClick={fetchData} className="btn-secondary">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      {status && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                <Database className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Total Tenants</p>
                <p className="text-xl font-bold text-white">{status.summary.total_tenants}</p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-green-600/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Current Version</p>
                <p className="text-xl font-bold text-white">{status.summary.current}</p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-yellow-600/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Behind / Unknown</p>
                <p className="text-xl font-bold text-white">
                  {status.summary.behind} / {status.summary.unknown}
                </p>
              </div>
            </div>
          </div>

          <div className="card p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-primary-600/20 rounded-lg flex items-center justify-center">
                <Database className="h-5 w-5 text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Latest Version</p>
                <p className="text-xl font-bold text-white">
                  {status.summary.latest_version || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tenant Status Table */}
      <div className="card">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Tenant Database Status</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                  Tenant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                  Database
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                  Schema Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                  Last Migration
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {status?.tenants.map((tenant) => (
                <tr key={tenant.tenant_id} className="hover:bg-gray-800/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <p className="text-white font-medium">{tenant.company_name}</p>
                      <p className="text-sm text-gray-500">{tenant.slug}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="text-sm text-gray-400 bg-gray-800 px-2 py-1 rounded">
                      {tenant.db_name}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-white font-mono">{tenant.current_version}</span>
                    {tenant.is_current && (
                      <CheckCircle className="h-4 w-4 text-green-400 inline ml-2" />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(tenant.is_current ? 'success' : tenant.current_version === 'unknown' ? 'unknown' : 'behind')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    {tenant.last_migration_at
                      ? new Date(tenant.last_migration_at).toLocaleString()
                      : 'Never'}
                  </td>
                </tr>
              ))}
              {(!status || status.tenants.length === 0) && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No tenant databases found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Migration History */}
      <div className="card">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Migration History</h2>
        </div>
        <div className="divide-y divide-gray-700">
          {runs.map((run) => (
            <div key={run.id}>
              <button
                onClick={() => loadRunDetails(run.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-800/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  {getStatusIcon(run.status)}
                  <div className="text-left">
                    <p className="text-white font-medium">
                      v{run.migration_version} - {run.migration_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(run.started_at).toLocaleString()} via {run.trigger_type}
                      {run.dry_run && ' (dry run)'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-sm">
                      <span className="text-green-400">{run.tenants_success}</span>
                      {' / '}
                      <span className="text-red-400">{run.tenants_failed}</span>
                      {' / '}
                      <span className="text-gray-400">{run.tenants_skipped}</span>
                    </p>
                    <p className="text-xs text-gray-500">success / failed / skipped</p>
                  </div>
                  {run.github_run_url && (
                    <a
                      href={run.github_run_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-gray-400 hover:text-white"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                  {expandedRun === run.id ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </button>

              {expandedRun === run.id && runDetails[run.id] && (
                <div className="px-6 pb-4 bg-gray-800/30">
                  <table className="min-w-full">
                    <thead>
                      <tr className="text-xs text-gray-500 uppercase">
                        <th className="py-2 text-left">Tenant</th>
                        <th className="py-2 text-left">Status</th>
                        <th className="py-2 text-left">Duration</th>
                        <th className="py-2 text-left">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/50">
                      {runDetails[run.id].map((detail: any) => (
                        <tr key={detail.id} className="text-sm">
                          <td className="py-2 text-white">{detail.slug}</td>
                          <td className="py-2">{getStatusBadge(detail.status)}</td>
                          <td className="py-2 text-gray-400">
                            {detail.execution_time_ms ? `${detail.execution_time_ms}ms` : '-'}
                          </td>
                          <td className="py-2 text-red-400 text-xs max-w-md truncate">
                            {detail.error_message || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
          {runs.length === 0 && (
            <div className="px-6 py-12 text-center text-gray-500">
              No migration runs recorded yet
            </div>
          )}
        </div>
      </div>

      {/* Available Migrations */}
      {status && status.available_migrations.length > 0 && (
        <div className="card">
          <div className="p-6 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">Available Migrations</h2>
          </div>
          <div className="p-6">
            <div className="space-y-2">
              {status.available_migrations.map((migration) => (
                <div
                  key={migration.version}
                  className="flex items-center gap-3 text-sm"
                >
                  <code className="text-primary-400 font-mono">v{migration.version}</code>
                  <span className="text-gray-400">{migration.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Running Migrations</h3>
        <div className="text-sm text-gray-400 space-y-3">
          <p>
            Migrations are run via GitHub Actions. To trigger a migration:
          </p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>Go to Actions &gt; "Migrate Tenant Databases"</li>
            <li>Click "Run workflow"</li>
            <li>Enter the migration version (e.g., "001")</li>
            <li>Select environment (staging/production)</li>
            <li>Optionally enable dry run to preview changes</li>
          </ol>
          <p className="mt-4">
            <span className="text-yellow-400">Important:</span> Always run migrations on staging first
            and verify results before running on production.
          </p>
        </div>
      </div>
    </div>
  )
}
